import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormLabel,
  FormGroup,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  TextField,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { SquarePen, X, Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import api from "../../axios/api";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import CustomButton from "../../componentsII/Buttons";
import { useSelector } from "react-redux";
import { PenBoxIcon } from "lucide-react";

const formSchema = yup.object().shape({
  locationName: yup
    .string()
    .min(2, "Location name is required")
    .required("Location name is required"),
  address: yup.string().required("Address is required"),
  country: yup.string().required("Country is required"),
  city: yup.string().required("City is required"),
  state: yup.string().required("State is required"),
  zipCode: yup.string().required("Zip code is required"),
  features: yup.array().of(yup.string()).optional(),
  grossArea: yup.string().required("Gross area is required"),
  areaUnit: yup.string().required("Unit is required"),
  primaryUse: yup.string().required("Primary use is required"),
  subtype_usage_id: yup.string().required("Occupancy type is required"),
  usingNaturalGas: yup.string().required("Natural gas usage is required"),
  usingHeatSteam: yup.string().required("Heat/steam usage is required"),
  usingPurchasedCooling: yup
    .string()
    .required("Purchased cooling usage is required"),
});

const LocationDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [activeTab, setActiveTab] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [primaryUses, setPrimaryUses] = useState([]);
  const [primaryUseTypes, setPrimaryUseTypes] = useState([]);
  const [areaUnits, setAreaUnits] = useState([]);
  const dispatch = useDispatch();

  const [isCancelClicked, setIsCancelClicked] = useState(0);
  const year = useSelector((state) => state.user.selectedYear);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      id: initialData?.id || "",
      comp_data_year_id: initialData.comp_data_year_id || "",
      company_id: initialData?.company_id || "",
      locationName: initialData?.loc_name || "",
      address: initialData?.address_line_1 || "",
      country: initialData?.country?.country_id || "",
      state: initialData?.state?.state_id || "",
      city: initialData?.city_name || "",
      zipCode: initialData?.zipcode || "",
      grossArea: initialData?.gross_area || "",
      areaUnit: initialData?.unit?.unit_id || "",
      primaryUse: initialData?.primary_usage?.primary_usage_id || "",
      subtype_usage_id:
        initialData?.primary_usage_type?.primary_usage_type_id || "",
      usingNaturalGas: initialData?.using_natural_gas === "1",
      usingHeatSteam: initialData?.using_heat_steam === "1",
      usingPurchasedCooling: initialData?.using_purchased_cooling === "1",
    },
  });

  const watchCountry = watch("country");
  const watchPrimaryUse = watch("primaryUse");

  const handleEdit = () => {
    setIsEditable(!isEditable);
    if (!isEditable) {
      fetchDropdownData();
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [countryRes, primaryUseRes, areaUnitRes] = await Promise.all([
        api.get("/common/countryDrop"),
        api.get("/common/locationprimaryusage"),
        api.get("/common/measurementtype?type_code=AREA"),
      ]);
      setCountries(countryRes.data.data);
      setPrimaryUses(primaryUseRes.data.data);
      setAreaUnits(areaUnitRes.data.data);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      dispatch(
        showNotification({
          message: "Error fetching dropdown data",
          type: "error",
        })
      );
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const res = await api.get(`/common/statedrop?country_id=${countryId}`);
      setStates(res.data.data);
    } catch (error) {
      setStates([]);
      console.error("Error fetching states:", error);
      dispatch(
        showNotification({ message: "Error fetching states", type: "error" })
      );
    }
  };

  const fetchPrimaryUseTypes = async (primaryUseId) => {
    try {
      const res = await api.get(
        `/common/locationprimaryusagetype?primary_usage_id=${primaryUseId}`
      );
      setPrimaryUseTypes(res.data.data);
    } catch (error) {
      console.error("Error fetching primary use types:", error);
      dispatch(
        showNotification({
          message: "Error fetching primary use types",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (watchCountry) {
      fetchStates(watchCountry);
      // Only reset state if it's not initial load
      if (!isInitialLoad) {
        setValue("state", undefined);
      }
    }
  }, [watchCountry]);

  useEffect(() => {
    if (watchPrimaryUse) {
      fetchPrimaryUseTypes(watchPrimaryUse);

      // Only reset subtype if it's not initial load
      if (!isInitialLoad) {
        setValue("subtype_usage_id", undefined);
      }
    }
  }, [watchPrimaryUse]);

  // Add useEffect to handle initial data loading
  useEffect(() => {
    if (initialData && isInitialLoad) {
      // Fetch initial dropdowns
      fetchDropdownData();

      if (initialData.country?.country_id) {
        fetchStates(initialData.country.country_id);
      }

      if (initialData.primary_usage?.primary_usage_id) {
        fetchPrimaryUseTypes(initialData.primary_usage.primary_usage_id);
      }

      // Set isInitialLoad to false after initial setup
      setIsInitialLoad(false);
    }
  }, [initialData]);

  const handleReset = () => {
    reset();
    setIsEditable(false);
  };

  const handleCancelEdit = () => {
    reset({
      id: initialData?.id || "",
      company_id: initialData?.company_id || "",
      comp_data_year_id: initialData?.comp_data_year_id || "",
      locationName: initialData?.loc_name || "",
      address: initialData?.address_line_1 || "",
      country: initialData?.country?.country_id || "",
      state: initialData?.state?.state_id || "",
      city: initialData?.city_name || "",
      zipCode: initialData?.zipcode || "",
      grossArea: initialData?.gross_area || "",
      areaUnit: initialData?.unit?.unit_id || "",
      primaryUse: initialData?.primary_usage?.primary_usage_id || "",
      subtype_usage_id:
        initialData?.primary_usage_type?.primary_usage_type_id || "",
      usingNaturalGas: initialData?.using_natural_gas === "1",
      usingHeatSteam: initialData?.using_heat_steam === "1",
      usingPurchasedCooling: initialData?.using_purchased_cooling === "1",
    });
    setIsEditable(false);
  };

  const onSubmit = async (data) => {
    try {
      const formattedData = {
        id: data.id,
        company_id: data.company_id,
        comp_data_year_id: data.comp_data_year_id || 1,
        loc_short_code: data.locationName.substring(0, 5).toUpperCase(),
        loc_name: data.locationName,
        address_line_1: data.address,
        country_id: data.country,
        state_id: data.state,
        city_name: data.city,
        zipcode: data.zipCode,
        gross_area: data.grossArea,
        gross_area_unit: data.areaUnit,
        primary_usage_id: data.primaryUse,
        subtype_usage_id: data.subtype_usage_id,
        using_natural_gas: data.usingNaturalGas === "true" ? "1" : "0",
        using_heat_steam: data.usingHeatSteam === "true" ? "1" : "0",
        using_purchased_cooling:
          data.usingPurchasedCooling === "true" ? "1" : "0",
      };

      const response = await api.post(
        `/company/addupdatecompanylocation/`,
        formattedData
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: "Location updated successfully",
            type: "success",
          })
        );
        refetch();
        setIsEditable(false);
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: error.response?.data?.message || "Error updating location",
          type: "error",
        })
      );
    }
  };

  const UtilitiesSection = () => {
    const watchUtilities = {
      usingNaturalGas: watch("usingNaturalGas"),
      usingHeatSteam: watch("usingHeatSteam"),
      usingPurchasedCooling: watch("usingPurchasedCooling"),
    };

    return (
      <div className="border-b border-gray-500 pt-4 mt-0">
        <FormControl component="fieldset">
          <FormLabel
            component="legend"
            className="text-gray-800 font-medium mb-3"
          >
            In addition to electricity, does your location use any of the
            following utilities?
          </FormLabel>
          <FormGroup>
            <>
              <Controller
                name="usingNaturalGas"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Natural Gas Usage"
                    disabled={!isEditable}
                  />
                )}
              />
              <Controller
                name="usingHeatSteam"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Heat/Steam Usage"
                    disabled={!isEditable}
                  />
                )}
              />
              <Controller
                name="usingPurchasedCooling"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Purchased Cooling Usage"
                    disabled={!isEditable}
                  />
                )}
              />
            </>
          </FormGroup>
        </FormControl>
      </div>
    );
  };

  const getOptionLabel = (options, value) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : "-";
  };

  const DetailRowEditable = ({ title, name, type = "text", options = [], isBorderBottom = true, className = "", required = true, handleChange=()=>{} }) => {
    return (
      <div className={`h-[70px] pt-1 w-full items-center ${isBorderBottom && !isEditable ? "border-b border-gray-500" : ""} ${className}`}>
        <div className="font-medium text-gray-900 mt-1 flex items-center gap-1">
          {title}
          <p className={required ? "text-red-500" : "invisible"}>*</p>
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            isEditable ? (
              type === "select" ? (
                <FormControl fullWidth size="small" error={!!errors[name]}>
                  <Select
                    {...field}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 250 }
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedVal = options.find((item) => item?.value == selected)
                      if (selectedVal == undefined) {
                        return <em>Select option</em>;
                      }
                      return selectedVal?.label;
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem key={option.id || option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors[name] && <FormHelperText>{errors[name].message}</FormHelperText>}
                </FormControl>
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  error={!!errors[name]}
                  helperText={errors[name]?.message}
                />
              )
            ) : (
              <div className='mb-1'>
                {type === 'select'
                  ? options.find(opt => opt.value === field.value)?.label || '-'
                  : (field.value || '-')}
              </div>
            )
          )}
        />
      </div>
    );
  };

  return (
    <div
      className="!max-h-fit overflow-y-auto w-full"
      style={{ height: "calc(100vh - 150px" }}
    >
      <div>
        <div className="flex border-b border-dashed border-gray-500 h-1/2">
          <div className="w-[20%] border-r border-gray-500">
            <img
              src={detailModalData?.location}
              alt="Location"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="px-8">
            <h1 className="text-2xl font-bold text-green-600">
              {detailModalData?.loc_name}
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium">
              {detailModalData?.city_name}, {detailModalData?.state_name}
            </p>
            <div className="flex">
              <ButtonGroup
                variant=""
                aria-label="Basic button group"
                style={{ border: "2px solid #388E3C" }}
              >
                <Button
                  style={{
                    backgroundColor: activeTab === 0 ? "#388E3C" : "",
                    color: activeTab === 0 ? "#fff" : "",
                  }}
                  color="green"
                  onClick={() => setActiveTab(0)}
                >
                  Details
                </Button>
                <Button
                  style={{
                    backgroundColor: activeTab === 1 ? "#388E3C" : "",
                    color: activeTab === 1 ? "#fff" : "",
                  }}
                  color="gray"
                  onClick={() => setActiveTab(1)}
                >
                  Usage data
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)} className="relative">
            {activeTab === 0 ? (
              <div className="px-6">
                <div className="flex justify-between w-full items-center">
                  <div className="text-gray-800 my-3 text-xl font-bold">
                    Location Details
                  </div>
                  {!isEditable ? (
                    <div className="cursor-pointer" onClick={handleEdit}>
                      <SquarePen color="#16a34a" />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <>
                  <DetailRowEditable
                    title="Location Name"
                    name="locationName"
                  />
                  <DetailRowEditable title="Address" name="address" />
                  <div className={`flex gap-4 ${!isEditable ? "border-b border-gray-500" : ""} `}>
                    <DetailRowEditable
                      title="Country"
                      name="country"
                      type="select"
                      options={countries}
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="State"
                      name="state"
                      type="select"
                      options={states}
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="City"
                      name="city"
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="Zip Code"
                      name="zipCode"
                      isBorderBottom={false}
                    />
                  </div>
                  <div className={`flex gap-4 ${!isEditable ? "border-b border-gray-500" : ""} `}>
                    <DetailRowEditable
                      title="Gross Area"
                      name="grossArea"
                      type="text"
                      className="flex-1"
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="Area Unit"
                      name="areaUnit"
                      type="select"
                      options={areaUnits}
                      className="flex-1"
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="Primary Use"
                      name="primaryUse"
                      className="flex-1"
                      type="select"
                      options={primaryUses}
                      isBorderBottom={false}
                    />
                    <DetailRowEditable
                      title="Primary Use Type"
                      name="subtype_usage_id"
                      className="flex-1"
                      type="select"
                      options={primaryUseTypes}
                      isBorderBottom={false}
                    />
                  </div>
                  <UtilitiesSection />
                </>

                <div
                  className="w-full flex justify-center items-center py-5 sticky bg-white border-t"
                  style={{ bottom: 0 }}
                >
                  {isEditable ? (
                    <div className="w-full flex justify-end gap-5 items-center">
                      <CustomButton
                        className="bg-red-500 hover:bg-red-700 text-white "
                        click={() => {
                          reset();
                          fetchPrimaryUseTypes(initialData?.primary_usage?.primary_usage_id);
                          fetchStates(initialData?.country?.country_id);
                          setValue(
                            "subtype_usage_id",
                            initialData?.primary_usage_type?.primary_usage_type_id
                          );
                          setValue("state", initialData?.country?.state_id);
                          handleCancelEdit();
                        }}
                        text="Cancel"
                      />
                      <CustomButton
                        className="bg-green-600 hover:bg-green-700 text-white "
                        type="submit"
                        text="Submit"
                      />
                    </div>
                  ) : (
                    <CustomButton
                      className="bg-red-500 hover:bg-red-700 text-white "
                      click={() => setIsDeleteModalOpen(true)}
                      text="Delete"
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6">
               
<div>
  <div className="grid grid-cols-2">
    {detailModalData?.Electricity_consumption?.electricity_qty ||
    detailModalData?.Heat_steam_consumption?.heatsteem_qty ||
    detailModalData?.Natural_Gas_consumption?.gas_qty ||
    detailModalData?.Purchased_cooling_consumption?.cooling_qty ? (
      <div>
        <div className="flex gap-2 items-center">
          <p className="font-medium text-[20px]">
            {(
              (Number(detailModalData?.Electricity_consumption?.co2_emission_tones) || 0) +
              (Number(detailModalData?.Heat_steam_consumption?.co2_emission_tones) || 0) +
              (Number(detailModalData?.Natural_Gas_consumption?.co2_emission_tones) || 0) +
              (Number(detailModalData?.Purchased_cooling_consumption?.co2_emission_tones) || 0)
            ).toFixed(2)}
          </p>
          <p>
            tCO<sub>2</sub>e
          </p>
        </div>
        <p>Total emission for this equipment</p>
      </div>
    ) : null}
  </div>

  <>
    {(detailModalData?.Electricity_consumption ||
      detailModalData?.Heat_steam_consumption ||
      detailModalData?.Natural_Gas_consumption ||
      detailModalData?.Purchased_cooling_consumption) ? (
      <>
        {detailModalData?.Electricity_consumption && (
          <div>
            <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
              Electricity Consumption
               {/* <PenBoxIcon size={20} /> */}
            </h3>
            {detailModalData?.Electricity_consumption?.electricity_qty ? (
              <div className="grid grid-cols-3 h-10 w-full items-center border-gray-500">
                <div className="font-normal text-gray-900">Electricity</div>
                <div>{detailModalData.Electricity_consumption.electricity_qty || "--"} {detailModalData.Electricity_consumption.unit.unit_label || "--"}</div>
                <div>{detailModalData.Electricity_consumption.co2_emission_tones || "--"} <span>tCO<sub>2</sub>e</span></div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20">
                <p className="text-gray-500">No Electricity usage data available for the equipment</p>
              </div>
            )}
          </div>
        )}

        {detailModalData?.Heat_steam_consumption && (
          <div>
            <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
              Heat & Steam Consumption 
              {/* <PenBoxIcon size={20} /> */}
            </h3>
            {detailModalData?.Heat_steam_consumption?.heatsteem_qty ? (
              <div className="grid grid-cols-3 h-10 w-full items-center border-gray-500">
                <div className="font-normal text-gray-900">Heat/Steam</div>
                <div>{detailModalData.Heat_steam_consumption.heatsteem_qty || "--"} {detailModalData.Heat_steam_consumption.unit.unit_label || "--"}</div>
                <div>{detailModalData.Heat_steam_consumption.co2_emission_tones || "--"} <span>tCO<sub>2</sub>e</span></div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20">
                <p className="text-gray-500">No Heat/Steam usage data available for the equipment</p>
              </div>
            )}
          </div>
        )}

        {detailModalData?.Natural_Gas_consumption && (
          <div>
            <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
              Natural Gas Consumption
               {/* <PenBoxIcon size={20} /> */}
            </h3>
            {detailModalData?.Natural_Gas_consumption?.gas_qty ? (
              <div className="grid grid-cols-3 h-10 w-full items-center border-gray-500">
                <div className="font-normal text-gray-900">Natural Gas</div>
                <div>{detailModalData.Natural_Gas_consumption.gas_qty || "--"} {detailModalData.Natural_Gas_consumption.unit.unit_label || "--"}</div>
                <div>{detailModalData.Natural_Gas_consumption.co2_emission_tones || "--"} <span>tCO<sub>2</sub>e</span></div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20">
                <p className="text-gray-500">No Natural Gas usage data available for the equipment</p>
              </div>
            )}
          </div>
        )}

        {detailModalData?.Purchased_cooling_consumption && (
          <div>
            <h3 className="font-medium text-[20px] pt-5 flex items-center gap-2">
              Purchased Cooling Consumption
               {/* <PenBoxIcon size={20} /> */}
            </h3>
            {detailModalData?.Purchased_cooling_consumption?.cooling_qty ? (
              <div className="grid grid-cols-3 h-10 w-full items-center border-gray-500">
                <div className="font-normal text-gray-900">Cooling</div>
                <div>{detailModalData.Purchased_cooling_consumption.cooling_qty || "--"} {detailModalData.Purchased_cooling_consumption.unit.unit_label || "--"}</div>
                <div>{detailModalData.Purchased_cooling_consumption.co2_emission_tones || "--"} <span>tCO<sub>2</sub>e</span></div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20">
                <p className="text-gray-500">No Cooling usage data available for the equipment</p>
              </div>
            )}
          </div>
        )}
      </>
    ) : (
      <div className="flex flex-col items-center justify-center h-80">
        <img src="/vectors/Equipment_emission_data.jpg" alt="No data" className="w-60 h-60 mb-4" />
        <p className="text-gray-500">No usage data available for the equipment</p>
      </div>
    )}
  </>
</div>



              </div>
            )}
          </form>
        </div>
      </div>

      <DeleteConfirmationModal
        isDeleteModalOpen={isDeleteModalOpen}
        handleCloseModal={() => setIsDeleteModalOpen(false)}
        handleOpenModal={() => setIsDeleteModalOpen(true)}
        id={detailModalData?.id}
        refetch={refetch}
        handleCloseMainDetailModal={handleCloseMainDetailModal}
      />
    </div>
  );
};

const DeleteConfirmationModal = ({
  isDeleteModalOpen,
  handleCloseModal,
  id,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();

  const deleteLocation = async () => {
    try {
      const response = await api.patch(`/company/deletecompanylocation/${id}`);
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      dispatch(
        showNotification({
          message: error.response?.data?.message || "Error deleting location",
          type: "error",
        })
      );
    }
    handleCloseModal();
  };

  return (
    <Dialog open={isDeleteModalOpen} onClose={handleCloseModal}>
      <DialogTitle>
        Delete Location
        <button
          onClick={handleCloseModal}
          className="absolute z-50 top-2 right-2 text-gray-600 hover:text-gray-800"
          aria-label="Close"
        >
          <X size={24} />
        </button>
      </DialogTitle>
      <DialogContent>
        <p>Are you sure you want to delete this location?</p>
        <p>This action cannot be undone.</p>
      </DialogContent>
      <DialogActions>
        <div className="w-full flex justify-end gap-5 items-center px-5 pb-5">
          <CustomButton
            className="bg-red-500 hover:bg-red-700 text-white "
            click={() => handleCloseModal()}
            text="Cancel"
          />
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white "
            click={() => deleteLocation()}
            text="Delete"
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default LocationDetailModal;
